/**
 * BillingPortalPage.jsx — /governance/billing
 * Área autenticada de gerenciamento de assinatura Stripe.
 *
 * Fluxo:
 *   - Carrega status da assinatura via GET /api/billing/stripe/subscription-status
 *   - Exibe plano ativo, origem (Stripe / Azure Marketplace), período e status
 *   - Botão "Gerenciar cobrança" → POST /api/billing/stripe/create-portal-session
 *     → redireciona para Stripe Customer Portal
 *   - Botão "Ver planos" → /pricing (upgrade/downgrade pelo portal Stripe)
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AZURE } from '../../services/api';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

// ─── Badge de status ──────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active:    { label: 'Ativa',       bg: '#d1fae5', color: '#065f46' },
    trialing:  { label: 'Trial',       bg: '#dbeafe', color: '#1d4ed8' },
    past_due:  { label: 'Inadimplente',bg: '#fef3c7', color: '#92400e' },
    canceled:  { label: 'Cancelada',   bg: '#fee2e2', color: '#991b1b' },
    inactive:  { label: 'Inativa',     bg: '#f3f4f6', color: '#6b7280' },
  };
  const s = map[status] || map.inactive;
  return (
    <span
      className="inline-block px-3 py-0.5 rounded-full text-xs font-bold"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

// ─── Badge de origem ──────────────────────────────────────────────────────────
const SourceBadge = ({ source }) => {
  const map = {
    stripe:            { label: '💳 Stripe',            bg: '#ede9fe', color: '#5b21b6' },
    azure_marketplace: { label: '☁️ Azure Marketplace', bg: '#dbeafe', color: '#1d4ed8' },
    manual:            { label: '📋 Manual',             bg: '#f3f4f6', color: '#374151' },
    trial:             { label: '🎁 Trial',              bg: '#d1fae5', color: '#065f46' },
  };
  const s = map[source] || { label: source, bg: '#f3f4f6', color: '#374151' };
  return (
    <span
      className="inline-block px-3 py-0.5 rounded-full text-xs font-bold"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

// ─── Card de entitlement ──────────────────────────────────────────────────────
const EntitlementCard = ({ ent }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-start justify-between mb-3">
      <div>
        <p className="font-bold text-gray-900 text-sm">
          {ent.plan_display_name || ent.plan_code}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">{ent.product_code}</p>
      </div>
      <div className="flex gap-2 flex-wrap justify-end">
        <StatusBadge status={ent.status} />
        <SourceBadge source={ent.source} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3 text-xs text-gray-500 mt-3">
      {ent.valid_from && (
        <div>
          <span className="font-semibold block">Início</span>
          {new Date(ent.valid_from).toLocaleDateString('pt-BR')}
        </div>
      )}
      {ent.valid_until && (
        <div>
          <span className="font-semibold block">Próxima cobrança</span>
          {new Date(ent.valid_until).toLocaleDateString('pt-BR')}
        </div>
      )}
      {ent.billing_interval && (
        <div>
          <span className="font-semibold block">Ciclo</span>
          {ent.billing_interval === 'monthly' ? 'Mensal' : 'Anual'}
        </div>
      )}
      {ent.unit_amount && (
        <div>
          <span className="font-semibold block">Valor</span>
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
            ent.unit_amount / 100
          )}
        </div>
      )}
      {ent.cancel_at_period_end ? (
        <div className="col-span-2 text-yellow-600 font-semibold">
          ⚠️ Cancelamento agendado para o fim do período
        </div>
      ) : null}
    </div>
  </div>
);

// ─── Página principal ─────────────────────────────────────────────────────────
const BillingPortalPage = () => {
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await AZURE.get('/billing/stripe/subscription-status');
        if (data.success) setStatusData(data.data);
        else setError(data.error || 'Erro ao carregar status.');
      } catch (err) {
        setError('Erro ao conectar com o servidor.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await AZURE.post('/billing/stripe/create-portal-session');
      if (data.success && data.data?.portal_url) {
        window.location.href = data.data.portal_url;
      } else {
        alert(data.error || 'Erro ao abrir portal de cobrança.');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao abrir portal de cobrança.');
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div
        className="py-8 px-6"
        style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #047857 100%)` }}
      >
        <div className="max-w-3xl mx-auto">
          <p className="text-sm text-green-200 mb-1">Governance › Financeiro</p>
          <h1 className="text-2xl font-extrabold text-white">Minha Assinatura</h1>
          <p className="text-sm text-green-100 mt-1">
            Gerencie seu plano, método de pagamento e histórico de faturas.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-28 bg-gray-200 animate-pulse rounded-xl" />
            ))}
          </div>
        )}

        {/* Erro */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm">
            ⚠️ {error}
          </div>
        )}

        {/* Conteúdo */}
        {!loading && statusData && (
          <>
            {/* Entitlements ativos */}
            {statusData.active_entitlements?.length > 0 ? (
              <section>
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                  Planos Ativos
                </h2>
                <div className="space-y-3">
                  {statusData.active_entitlements.map((ent, i) => (
                    <EntitlementCard key={i} ent={ent} />
                  ))}
                </div>
              </section>
            ) : (
              <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center">
                <p className="text-gray-500 text-sm mb-4">Nenhum plano ativo encontrado.</p>
                <Link
                  to="/pricing"
                  className="inline-block px-6 py-2.5 rounded-xl font-bold text-white"
                  style={{ backgroundColor: BRAND_GREEN }}
                >
                  Ver planos disponíveis →
                </Link>
              </div>
            )}

            {/* Ações */}
            <section>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                Ações
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Manage via Stripe Portal (só se tem customer Stripe) */}
                {statusData.has_stripe_customer && (
                  <button
                    onClick={handlePortal}
                    disabled={portalLoading}
                    className="flex items-center justify-center gap-2 bg-white border-2 rounded-xl py-3 px-5 font-bold text-sm transition-all hover:shadow-md disabled:opacity-50"
                    style={{ borderColor: BRAND_GOLD, color: BRAND_GOLD }}
                  >
                    {portalLoading ? '⏳ Abrindo...' : '💳 Gerenciar cobrança (Stripe)'}
                  </button>
                )}

                {/* Upgrade / novo plano */}
                <Link
                  to="/pricing"
                  className="flex items-center justify-center gap-2 border-2 rounded-xl py-3 px-5 font-bold text-sm transition-all hover:shadow-md"
                  style={{ borderColor: BRAND_GREEN, color: BRAND_GREEN }}
                >
                  🚀 Ver planos / Upgrade
                </Link>
              </div>
            </section>

            {/* Info Azure Marketplace */}
            {statusData.active_entitlements?.some((e) => e.source === 'azure_marketplace') && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                <strong>Assinatura via Azure Marketplace</strong> — Para gerenciar faturamento
                Microsoft, acesse seu{' '}
                <a
                  href="https://portal.azure.com"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-bold"
                >
                  portal Azure
                </a>
                .
              </div>
            )}

            {/* Aviso de pagamento falhado */}
            {statusData.active_entitlements?.some((e) => e.status === 'past_due') && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 text-sm text-yellow-800">
                ⚠️ <strong>Pagamento pendente.</strong> Atualize seu método de pagamento para
                manter o acesso.
                {statusData.has_stripe_customer && (
                  <button
                    onClick={handlePortal}
                    className="ml-2 underline font-bold"
                  >
                    Atualizar agora
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BillingPortalPage;
